import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  Output,
  Signal
} from '@angular/core';
import {
  DialogService,
  EControlActions,
  EsvgFiles,
  IControlDataChanged,
  IControlObject,
  ISelectFormOption,
  ITableAction,
  TableControlComponent,
  TableControlModule,
  TDBObject,
} from 'frontier';
import ControlGuids from '../../../api/ControlGuids';
import {DictionaryTableService} from '../../../api/generated';
import {Observable, of, Unsubscribable} from 'rxjs';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {LanguageSelectionDialogComponent} from './language-selection-dialog/language-selection-dialog.component';

type TDefault = 'Deutsch' | 'Englisch' | 'Custom'

@Component({
  selector: 'app-translation-table',
  standalone: true,
  templateUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  imports: [TableControlModule],
  providers: [{
    provide: TableControlComponent,
    useExisting: TranslationTableComponent,
  }],
})
export class TranslationTableComponent extends TableControlComponent {
  private _dialogService: DialogService = inject(DialogService);
  dictionaryTableService: DictionaryTableService = inject(DictionaryTableService);
  languageRepo: InputSignal<ISelectFormOption[]> = input.required<ISelectFormOption[]>();
  alertObjects: InputSignal<IControlObject[]> = input.required<IControlObject[]>();

  @Output() resolvedLanguage: EventEmitter<IControlObject> = new EventEmitter<IControlObject>();

  selectedLanguages: Signal<ISelectFormOption[]> = computed((): ISelectFormOption[] => {
    const languages: ISelectFormOption[] = this.languageRepo();
    return languages.some((language: ISelectFormOption): boolean => language?.value.isSelected) ?
      languages.filter((language: ISelectFormOption): boolean => language.value.isSelected) : languages;
  });
  defaultLanguage: Signal<ISelectFormOption> = computed((): ISelectFormOption => {
    const languages: ISelectFormOption[] = this.languageRepo();
    return languages.find((language: ISelectFormOption): boolean => language?.value.isDefault);
  });
  outdatedLanguages: Signal<ISelectFormOption[]> = computed((): ISelectFormOption[] => {
    const selectedLanguages: ISelectFormOption[] = this.selectedLanguages();
    const alertObjects: IControlObject[] = this.alertObjects();
    return selectedLanguages.filter((language: ISelectFormOption): boolean => alertObjects
      .some((alertObject: IControlObject): boolean => alertObject.signature === language?.value.signature));
  });
  override GUID: string = ControlGuids.TDictionaryTable;
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Neues Wörterbuch (leer)',
      controlAction: EControlActions.variantCreate,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.add,
      tooltip: 'Legt ein neues leeres Übersetzungsdokument an.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.createNewDictionary(),
      isHidden: (): boolean => this.rowCount > 0,
    },
    {
      displayName: 'Neues Wörterbuch (von Standard)',
      controlAction: EControlActions.createFromDefault,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.add,
      tooltip: 'Legt ein neues mit einer Standardsprache befülltes Übersetzungsdokument an.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.createNewDictionaryFromDefault(),
      isHidden: (): boolean => this.rowCount > 0,
    },
    {
      displayName: 'Neues Wörterbuch (von gewählter Sprache)',
      controlAction: EControlActions.createCustom,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.add,
      tooltip: 'Legt ein neues mit einer nachfolgend selektierten Sprache befülltes Übersetzungsdokument an.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.createNewDictionaryFromLanguage(),
      isHidden: (): boolean => this.rowCount > 0,
    },
    {
      displayName: 'Mit Standard überschreiben',
      controlAction: EControlActions.rerollAll,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.refresh,
      tooltip: 'Setzt die Übersetzungen auf den Wert einer Standardsprache zurück.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.overrideWithDefault(),
      isHidden: (): boolean => this.rowCount === 0,
    },
    {
      displayName: 'Mit Standard auffüllen',
      controlAction: EControlActions.editAll,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.edit,
      tooltip: 'Befüllt fehlende Übersetzungen mit dem Wert einer Standardsprache.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.createNewDictionaryFromDefault(),
      isHidden: (): boolean => this.rowCount === 0,
    },
    {
      displayName: 'Mit gewählter Sprache überschreiben',
      controlAction: EControlActions.reroll,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.refresh,
      tooltip: 'Setzt die Übersetzungen auf den Wert einer nachfolgend gewählten Sprache zurück.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.overrideWithLanguage(),
      isHidden: (): boolean => this.rowCount === 0,
    },
    {
      displayName: 'Mit gewählter Sprache auffüllen',
      controlAction: EControlActions.edit,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.edit,
      tooltip: 'Befüllt fehlende Übersetzungen mit dem Wert einer nachfolgend gewählten Sprache.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.createNewDictionaryFromLanguage(),
      isHidden: (): boolean => this.rowCount === 0,
    },
    {
      displayName: 'Schlüssel updaten',
      controlAction: EControlActions.resynchronize,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'warn',
      icon: EsvgFiles.reset,
      tooltip: 'Ergänzt fehlende Schlüssel, die der Datenbank kürzlich hinzugefügt wurden.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.updateKeys(),
      isHidden: (): boolean => this.outdatedLanguages().length === 0,
    },
  ]);

  constructor() {
    super();
    effect((): void => {
      const selectedLanguages: ISelectFormOption[] = this.selectedLanguages();
      if (selectedLanguages) {
        this.patchFilter({
          objectlist: selectedLanguages.filter((row: ISelectFormOption): TDBObject => row?.value)
            .map((row: ISelectFormOption): TDBObject => row.value),
          binaryfilter1: true, // needed for easy access backend-changes
        });
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [ControlGuids.TDictionaryTable, new Set([
              EControlActions.variantCreate,
              EControlActions.createFromDefault,
              EControlActions.createCustom,
            ])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): Unsubscribable => this.changeAndFetchInstance())).subscribe(),
    );

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [ControlGuids.TDictionaryTable, new Set([
              EControlActions.rerollAll,
              EControlActions.editAll,
              EControlActions.reroll,
              EControlActions.edit,
              EControlActions.resynchronize,
            ])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => this.refreshInfiniteCache())).subscribe(),
    );
  }

  createNewDictionary(): Observable<boolean> {
    return this.selectTargetLanguage().pipe(
      filter((mainLanguage: TDBObject): boolean => mainLanguage != null),
      switchMap((mainLanguage: TDBObject): Observable<boolean> =>
        this.dictionaryTableService.dictionaryTableCreateNewDictionary({MainLanguage: mainLanguage})
      ),
    );
  }

  createNewDictionaryFromDefault(): Observable<boolean> {
    return this.selectTargetLanguage().pipe(
      filter((mainLanguage: TDBObject): boolean => mainLanguage != null),
      switchMap((mainLanguage: TDBObject): Observable<{ mainLanguage: TDBObject, defaultLanguage: TDefault }> =>
        this.selectDefault().pipe(
          filter((defaultLanguage: TDefault): boolean => defaultLanguage != null),
          map((defaultLanguage: TDefault): { mainLanguage: TDBObject, defaultLanguage: TDefault } => {
            return ({mainLanguage, defaultLanguage})
          }),
        )
      ),
      switchMap(({mainLanguage, defaultLanguage}: { mainLanguage: TDBObject, defaultLanguage: TDefault })
        : Observable<boolean> =>
        this.dictionaryTableService.dictionaryTableCreateNewFromDefault({
          MainLanguage: mainLanguage,
          UseEnglish: defaultLanguage === 'Englisch',
          UseGerman: defaultLanguage === 'Deutsch',
        })
      ),
    );
  }

  createNewDictionaryFromLanguage(): Observable<boolean> {
    return this.selectTargetLanguage().pipe(
      filter((mainLanguage: TDBObject): boolean => mainLanguage != null),
      switchMap((mainLanguage: TDBObject): Observable<{ mainLanguage: TDBObject, originLanguage: TDBObject }> =>
        this.selectOriginLanguage(mainLanguage).pipe(
          filter((originLanguage: TDBObject): boolean => originLanguage != null),
          map((originLanguage: TDBObject): { mainLanguage: TDBObject, originLanguage: TDBObject } => {
            return ({mainLanguage, originLanguage})
          }),
        )
      ),
      switchMap(({mainLanguage, originLanguage}: { mainLanguage: TDBObject, originLanguage: TDBObject })
        : Observable<boolean> =>
        this.dictionaryTableService.dictionaryTableCreateNewFromLanguage({
          MainLanguage: mainLanguage,
          Language: originLanguage,
        })
      ),
    );
  }

  overrideWithDefault(): Observable<boolean> {
    return this.selectTargetLanguage().pipe(
      filter((mainLanguage: TDBObject): boolean => mainLanguage != null),
      switchMap((mainLanguage: TDBObject): Observable<{ mainLanguage: TDBObject, defaultLanguage: TDefault }> =>
        this.selectDefault().pipe(
          filter((defaultLanguage: TDefault): boolean => defaultLanguage != null),
          map((defaultLanguage: TDefault): { mainLanguage: TDBObject, defaultLanguage: TDefault } => {
            return ({mainLanguage, defaultLanguage})
          }),
        )
      ),
      switchMap(({mainLanguage, defaultLanguage}: { mainLanguage: TDBObject, defaultLanguage: TDefault })
        : Observable<boolean> =>
        this.dictionaryTableService.dictionaryTableOverrideWithDefault({
          MainLanguage: mainLanguage,
          UseEnglish: defaultLanguage === 'Englisch',
          UseGerman: defaultLanguage === 'Deutsch',
        })
      ),
    );
  }

  overrideWithLanguage(): Observable<boolean> {
    return this.selectTargetLanguage().pipe(
      filter((mainLanguage: TDBObject): boolean => mainLanguage != null),
      switchMap((mainLanguage: TDBObject): Observable<{ mainLanguage: TDBObject, originLanguage: TDBObject }> =>
        this.selectOriginLanguage(mainLanguage).pipe(
          filter((originLanguage: TDBObject): boolean => originLanguage != null),
          map((originLanguage: TDBObject): { mainLanguage: TDBObject, originLanguage: TDBObject } => {
            return ({mainLanguage, originLanguage})
          }),
        )
      ),
      switchMap(({mainLanguage, originLanguage}: { mainLanguage: TDBObject, originLanguage: TDBObject })
        : Observable<boolean> =>
        this.dictionaryTableService.dictionaryTableOverrideWithLanguage({
          MainLanguage: mainLanguage,
          Language: originLanguage,
        })
      ),
    );
  }


  updateKeys(): Observable<boolean> {
    return this.selectOutdatedLanguage().pipe(
      filter((mainLanguage: TDBObject): boolean => mainLanguage != null),
      switchMap((mainLanguage: TDBObject): Observable<{ mainLanguage: TDBObject, defaultLanguage: TDefault }> =>
        this.selectDefault(true).pipe(
          filter((defaultLanguage: TDefault): boolean => defaultLanguage != null),
          map((defaultLanguage: TDefault): { mainLanguage: TDBObject, defaultLanguage: TDefault } => {
            return ({mainLanguage, defaultLanguage})
          }),
        )
      ),
      switchMap(({mainLanguage, defaultLanguage}: { mainLanguage: TDBObject, defaultLanguage: TDefault })
        : Observable<boolean> =>
        this.dictionaryTableService.dictionaryTableUpdateKeys({
          MainLanguage: mainLanguage,
          SetValues: defaultLanguage !== 'Custom',
          UseGerman: defaultLanguage === 'Deutsch',
        }).pipe(
          tap((res: boolean): void => {
            if (res) {
              this.resolvedLanguage.emit(mainLanguage);
            }
          }),
        )
      ),
    );
  }

  selectTargetLanguage(): Observable<ISelectFormOption> {
    if (this.selectedLanguages().length > 1) {
      return this._dialogService.dialog.open(LanguageSelectionDialogComponent, {
        data: {
          languages: this.selectedLanguages(),
          title: 'Sprache auswählen',
          description: 'Wählen Sie aus den selektierten Sprachen die aus, auf die sie die gewählte Aktion anwenden ' +
            'möchten.',
          confirmText: 'Führen Sie die gewählte Aktion mit der gewählten Sprache aus.',
          cancelText: 'Brechen Sie die gewählte Aktion ab.',
        },
      }).afterClosed();
    } else {
      return of(this.selectedLanguages()[0]?.value ?? null);
    }
  }

  selectOutdatedLanguage(): Observable<ISelectFormOption> {
    if (this.outdatedLanguages().length > 1) {
      return this._dialogService.dialog.open(LanguageSelectionDialogComponent, {
        data: {
          languages: this.outdatedLanguages(),
          title: 'Sprache auswählen',
          description: 'Wählen Sie aus den selektierten Sprachen, die nicht mehr aktuell sind, die aus, die Sie' +
            ' aktualisieren möchten.',
          confirmText: 'Aktualisieren Sie die gewählte Sprache.',
          cancelText: 'Brechen Sie die gewählte Aktion ab.',
        },
      }).afterClosed();
    } else {
      return of(this.outdatedLanguages()[0].value);
    }
  }

  selectOriginLanguage(mainLanguage: TDBObject): Observable<ISelectFormOption> {
    return this._dialogService.dialog.open(LanguageSelectionDialogComponent, {
      data: {
        languages: this.languageRepo()
          .filter((language: ISelectFormOption): boolean => language.value.signature !== mainLanguage.signature),
        title: 'Sprache auswählen',
        description: 'Wählen Sie aus, die Texte welcher Sprache Sie als Basis verwenden möchten.',
        confirmText: 'Führen Sie die gewählte Aktion mit der gewählten Sprache aus.',
        cancelText: 'Brechen Sie die gewählte Aktion ab.',
      },
    }).afterClosed();
  }

  selectDefault(keyUpdate: boolean = false): Observable<TDefault> {
    return this._dialogService.dialog.open(LanguageSelectionDialogComponent, {
      data: {
        languages: [
          {
            name: keyUpdate ? 'Leer' : 'Standardsprache: ' + this.defaultLanguage().name,
            value: 'Custom',
          },
          {
            name: 'Deutsch',
            value: 'Deutsch',
          },
          {
            name: 'Englisch',
            value: 'Englisch',
          },
        ],
        title: 'Standardsprache auswählen',
        description: keyUpdate ? 'Wählen Sie aus, wie Sie die Übersetzungen der neuen Schlüssel initialisieren möchten.'
          : 'Wählen Sie aus, welche Standardsprache Sie als Basis verwenden möchten. Die erste Option verwendet immer' +
          ' die von Ihnen konfigurierten Texte, während "Deutsch" und "Englisch" sich auf die von uns ' +
          'bereitgestellten initialen Texte beziehen.',
        confirmText: 'Führen Sie die gewählte Aktion mit der gewählten Standardsprache aus.',
        cancelText: 'Brechen Sie die gewählte Aktion ab.',
      },
    }).afterClosed();
  }
}
